<script setup lang="ts">
type Variant = 'primary' | 'teal' | 'ghost' | 'quiet' | 'danger';

withDefaults(
  defineProps<{
    variant?: Variant;
    sm?: boolean;
    block?: boolean;
    disabled?: boolean;
  }>(),
  { variant: 'primary', sm: false, block: false, disabled: false },
);

defineEmits<{ click: [MouseEvent] }>();
</script>

<template>
  <button
    class="dt-btn"
    :class="[`dt-btn--${variant}`, { 'dt-btn--sm': sm, 'dt-btn--block': block }]"
    :disabled="disabled"
    @click="(event) => $emit('click', event)"
  >
    <slot />
  </button>
</template>

<style scoped>
/* Forma vinda do protótipo (`.btn`): inline-flex centralizado com gap, para alinhar ícone + rótulo.
   Toda medida e cor vem do tema do app (tokens/theme-<app>.css); aqui não há valor literal. */
.dt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--dt-btn-gap);
  font-family: var(--dt-font-family);
  font-weight: var(--dt-btn-font-weight);
  font-size: var(--dt-btn-font-size);
  border-radius: var(--dt-btn-radius);
  min-height: var(--dt-btn-min-height);
  padding: var(--dt-btn-padding-y) var(--dt-btn-padding-x);
  border: none;
  cursor: pointer;
  transition: var(--dt-btn-transition);
}

.dt-btn--sm {
  min-height: var(--dt-btn-sm-min-height);
  padding: var(--dt-btn-sm-padding-y) var(--dt-btn-sm-padding-x);
  font-size: var(--dt-btn-sm-font-size);
  border-radius: var(--dt-btn-sm-radius);
}

/* Botão pequeno (dt-btn--sm).
   Alvo de toque de 48x48 (regra de responsividade web) sem alterar o desenho: a caixa clicável é
   ampliada por pseudo-elemento, apenas na vertical, para não invadir o controle vizinho da linha. */
.dt-btn--sm {
  position: relative;
}
.dt-btn--sm::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: var(--dt-touch-target-min);
  transform: translateY(-50%);
}

.dt-btn--block {
  width: 100%;
}

.dt-btn:disabled {
  opacity: var(--dt-btn-disabled-opacity);
  cursor: not-allowed;
}

.dt-btn--primary {
  background: var(--dt-btn-primary-bg);
  color: var(--dt-btn-primary-color);
}
.dt-btn--primary:hover:not(:disabled) {
  background: var(--dt-btn-primary-hover-bg);
  /* O fallback para --dt-btn-primary-color é obrigatório: só o tema do Técnico define
     --dt-btn-primary-hover-color, porque só o protótipo do Técnico troca o texto para branco no
     hover do primário (Docs/V2/Tecnicos/tecnico/styles.css:88). Operacional e Clínica não definem
     esse token e seus protótipos não alteram a cor do texto no hover; sem o fallback o var()
     ficaria inválido em computed-value time e a cor do texto desses dois apps degradaria. Com o
     fallback, eles mantêm exatamente o comportamento atual. */
  color: var(--dt-btn-primary-hover-color, var(--dt-btn-primary-color));
}

.dt-btn--teal {
  background: var(--dt-btn-teal-bg);
  color: var(--dt-btn-teal-color);
}
.dt-btn--teal:hover:not(:disabled) {
  background: var(--dt-btn-teal-hover-bg);
  color: var(--dt-btn-teal-hover-color);
}

.dt-btn--ghost {
  background: var(--dt-btn-ghost-bg);
  color: var(--dt-btn-ghost-color);
  border: var(--dt-btn-ghost-border);
}
/* Hover opt-in por tema: só o protótipo do Operacional declara hover em ghost/quiet/danger
   (op/styles.css:88-90). Clínica e Técnico definem os tokens repetindo o estado base, então a
   regra existe e não produz mudança — a inércia de cada protótipo é preservada. */
.dt-btn--ghost:hover:not(:disabled) {
  background: var(--dt-btn-ghost-hover-bg);
}

.dt-btn--quiet {
  background: var(--dt-btn-quiet-bg);
  color: var(--dt-btn-quiet-color);
  border: var(--dt-btn-quiet-border);
}
.dt-btn--quiet:hover:not(:disabled) {
  border-color: var(--dt-btn-quiet-hover-border);
}

.dt-btn--danger {
  background: var(--dt-btn-danger-bg);
  color: var(--dt-btn-danger-color);
}
.dt-btn--danger:hover:not(:disabled) {
  filter: var(--dt-btn-danger-hover-filter);
}
</style>
